/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState, useEffect, forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    useMultiStyleConfig,
    Box,
    IconButton,
    Badge,
    omitThemingProps,
    Menu,
    MenuButton,
    MenuDivider,
    createStylesContext,
    Link as ChakraLink,
    VisuallyHidden
} from '@chakra-ui/react'

import {useCurrentBasket} from '../../hooks/use-current-basket'

import {
    BasketIcon,
    HamburgerIcon,
    StorefinderIcon,
    SignoutIcon,
    HeartIcon,
    BrandLogoBlack
} from '../icons'
import {
    STORE_LOCATOR_HREF,
    WISHLIST_HREF,
    HOME_HREF,
    STORE_LOCATOR_IS_ENABLED,
    CART_HREF,
    CUSTOMER_SERVICE_HREF
} from '../../constants'

import useMultiSite from '../../hooks/use-multi-site'
import useEffectEvent from '../../hooks/use-effect-event'
import LocaleSelector from '../locale-selector'
import Link from '../link'
import HeaderSearch from './header-search'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import LoadingSpinner from '../loading-spinner'
import MyStokkeNavigationDropdown from '../../pages/my-stokke/partials/navigation-dropdown'
import CompositeAccountIcon from '../icons/composite-account-icon'
import useOcapiSite from '../../hooks/useOcapiSite'
import {AuthHelpers, useAuthHelper} from '@salesforce/commerce-sdk-react'
import useCmsContent from '../cms-content/hooks/use-cms-content'
import {useGetHeaderLinks} from '../../utils/utils'
import {useHasWishlistProducts} from '../../hooks/use-wish-list'
import InteractionStudioContent from '../../components/interaction-studio/content'

import CMSContent from '../cms-content'
import {TYPE_CONTENT_SLOT} from '../cms-content/hooks/use-cms-content'
import StokkeMenuList from '../custom-menu-list/StokkeMenuList'
import StokkeMenuItem from '../custom-menu-list/StokkeMenuItem'
import {Global} from '@emotion/react'
import prepareHtmlForInjection from '../cms-content/util/prepare-html-for-injection'

const [StylesProvider, useStyles] = createStylesContext('Header')

export const useHeaderStyles = useStyles

/**
 * Extract the AccountButton component so it can be reused as menu button and standalone button
 */
const AccountButton = forwardRef((props, ref) => {
    const {formatMessage} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const firstName = useMemo(
        () => (customer.firstName !== '' ? customer.firstName : ''),
        [customer]
    )

    const styles = useMultiStyleConfig('Header', props)
    return (
        <IconButton
            {...props}
            //unnecessary ARIA attribute aria-haspopup can create unexpected behavior across various configurations
            aria-haspopup={undefined}
            ref={ref}
            icon={<CompositeAccountIcon isActive={customer.isRegistered} />}
            variant="unstyled"
            {...styles.accountButton}
            aria-label={formatMessage({
                id: 'header.button.assistive_msg.my_account',
                defaultMessage: 'My Stokke'
            })}
            data-testid="header-account-icon"
            // cannot use the same trigger for tooltip and popover,
            // so use the title attribute to show tooltip on hover
            title={
                customer.isRegistered
                    ? formatMessage(
                          {
                              id: 'global.account.tooltip',
                              defaultMessage: 'Hi, {firstName}'
                          },
                          {firstName}
                      )
                    : // no label, no tooltip
                      null
            }
        />
    )
})
if (process.env.NODE_ENV !== 'production') {
    AccountButton.displayName = 'AccountButton'
}
AccountButton.propTypes = {
    onClick: PropTypes.func
}

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const Header = forwardRef((props, ref) => {
    const intl = useIntl()
    const {myStokkeEnabled, wishlistEnabled} = useOcapiSite().preferences
    const {data: customer} = useCurrentCustomer()
    const {
        data: basket,
        derivedData: {totalItems},
        isLoading: basketLoading
    } = useCurrentBasket()
    const {site, buildUrl} = useMultiSite()
    // there is some bug that scrolls whole page with 1-2 px when opening the header
    const headerHideShowOffset = 20
    const showWishlistIcon = useHasWishlistProducts()
    const {data: messageBar} = useCmsContent('message-bar', TYPE_CONTENT_SLOT)
    const {hasCmsModules: messageBarHasCmsModules} = useMemo(
        () => prepareHtmlForInjection(messageBar?.c_body),
        [messageBar?.c_body]
    )

    const headerVariant = messageBarHasCmsModules ? 'messageBar' : 'default'

    const styles = useMultiStyleConfig('Header', {...props, variant: headerVariant})
    const {
        children,
        onMenuClick,
        onMyAccountClick,
        onMyAccountMenuOpen,
        onLogoClick,
        onMyCartClick,
        onSearchClick,
        menuIcon: MenuIcon = HamburgerIcon,
        ...ownProps
    } = omitThemingProps(props)

    const {data: headerLinksTemplate} = useCmsContent('header-links')
    const headerLinks = useGetHeaderLinks(headerLinksTemplate)

    /* When scrolling downwards - hide header, upwards - show header */
    const scrollTop = useRef(0)
    const [hideHeader, setHideHeader] = useState(false)

    const logout = useAuthHelper(AuthHelpers.Logout)

    const onScroll = useEffectEvent(() => {
        requestAnimationFrame(() => {
            // show header when on top of pdp
            if (scrollY === 0) {
                setHideHeader(false)
                return
            }

            if (
                scrollY > ref?.current?.clientHeight &&
                Math.abs(scrollY - scrollTop.current) > headerHideShowOffset
            ) {
                setHideHeader(scrollY > scrollTop.current)

                scrollTop.current = scrollY
            }
        })
    })
    useEffect(() => {
        addEventListener('scroll', onScroll, {passive: true})

        /* Cleanup event listener */
        return () => removeEventListener('scroll', onScroll)
    }, [onScroll])

    const storeLocatorHref = useMemo(() => buildUrl(STORE_LOCATOR_HREF), [buildUrl])

    // my-stokke page will redirect to home in case logout happens on my-stokke page,
    // otherwise keep the current page

    const onSignOutClick = async () => {
        await logout.mutateAsync()
    }

    const wishlistHref = useMemo(() => buildUrl(WISHLIST_HREF), [buildUrl])

    return (
        <StylesProvider value={styles}>
            <Box
                bg="stokkeCore.white"
                // this is a workaround to prevent header shifts on modal/drawer open
                paddingInlineEnd="var(--removed-body-scroll-bar-size)"
                // show/hide the header
                transform={`translateY(${hideHeader ? 'calc(-1 * var(--header-height))' : '0px'})`}
                // animated
                transition="transform 0.5s"
                // disable pointer events when hidden to allow interaction with elements below
                // and enable when visible to override the parent's disabled pointer events
                pointerEvents={hideHeader ? 'none' : 'auto'}
            >
                <Global
                    styles={{
                        ':root': {
                            // signifies that header is rendered
                            '--has-header': '1',
                            // for proper height calculation expose if message bar is present
                            '--header-has-message-bar': messageBarHasCmsModules ? '1' : '0'
                            // NOTE: do not expose the header state on :root as that causes flickr during scrolling
                        },
                        // update top positioned toasts, as updating :root causes complete layout recalculation
                        // and that introduces flickr during scrolling
                        '#chakra-toast-manager-top-left, #chakra-toast-manager-top, #chakra-toast-manager-top-right':
                            {
                                transform: `translateY(${
                                    hideHeader ? 'calc(-1 * var(--header-height))' : '0px'
                                })`,
                                // matches header transition and makes for a smoother behavior when scrolling
                                transition: 'transform 0.5s;'
                            }
                    }}
                />
                {logout.isLoading && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}
                <Box ref={ref} {...styles.container} {...ownProps}>
                    {messageBarHasCmsModules && (
                        <InteractionStudioContent id="is-message-bar">
                            <CMSContent
                                id="message-bar"
                                type={TYPE_CONTENT_SLOT}
                                content={messageBar}
                                showError={false}
                            />
                        </InteractionStudioContent>
                    )}
                    <Box {...styles.headerContainer} as="header">
                        <Box {...styles.helperContainer}>
                            <Link to={CUSTOMER_SERVICE_HREF} size="xs" variant="underlineLink">
                                <FormattedMessage id="global.help" defaultMessage="Help" />
                            </Link>
                            {headerLinks?.map((item, index) => {
                                return (
                                    <ChakraLink
                                        href={
                                            item.type === 'Link'
                                                ? `${buildUrl(item.link)}`
                                                : item.link
                                        }
                                        key={'header-link-' + index}
                                        size="xs"
                                        variant="underlineLink"
                                    >
                                        {item.label}
                                    </ChakraLink>
                                )
                            })}
                            {STORE_LOCATOR_IS_ENABLED && (
                                <Link to={STORE_LOCATOR_HREF} size="xs" variant="underlineLink">
                                    <FormattedMessage id="global.stores" defaultMessage="Stores" />
                                </Link>
                            )}
                            <LocaleSelector variant="underlineLink" />
                        </Box>
                        <Box {...styles.mainContainer}>
                            <Link
                                href={HOME_HREF}
                                {...styles.brandButton}
                                variant="unstyled"
                                onClick={onLogoClick}
                                data-testid="logo-link"
                            >
                                <BrandLogoBlack
                                    {...styles.logo}
                                    title={intl.formatMessage({
                                        id: 'header.button.assistive_msg.logo',
                                        defaultMessage: 'Stokke homepage'
                                    })}
                                />
                            </Link>
                            <Box {...styles.leftContent}>
                                <IconButton
                                    aria-label={intl.formatMessage({
                                        id: 'header.button.assistive_msg.menu',
                                        defaultMessage: 'Menu'
                                    })}
                                    icon={<MenuIcon {...styles.icon} />}
                                    variant="unstyled"
                                    {...styles.menuButton}
                                    onClick={onMenuClick}
                                />
                                <Box {...styles.bodyContainer}>
                                    {React.Children.map(children, (child) =>
                                        React.cloneElement(child, {headerVariant: headerVariant})
                                    )}
                                </Box>
                            </Box>
                            <Box {...styles.rightContent}>
                                <HeaderSearch onShow={onSearchClick} />

                                <AccountButton
                                    display={{
                                        base: 'none',
                                        lg:
                                            myStokkeEnabled && customer.isRegistered
                                                ? 'none'
                                                : 'block'
                                    }}
                                    onClick={onMyAccountClick}
                                />

                                {myStokkeEnabled && customer.isRegistered && (
                                    <Menu
                                        placement="bottom-end"
                                        variant="menu"
                                        onOpen={onMyAccountMenuOpen}
                                    >
                                        <MenuButton
                                            display={{base: 'none', lg: 'block'}}
                                            as={AccountButton}
                                        />

                                        <StokkeMenuList
                                            rootProps={{display: {base: 'none', lg: 'block'}}}
                                            data-testid="header-account-menu"
                                            // offset to the right to accommodate the menu button chevron
                                            // 1 unit from gap and 2 units from chevron size
                                            marginInlineEnd={-3}
                                        >
                                            <MyStokkeNavigationDropdown />
                                            <MenuDivider />
                                            <StokkeMenuItem
                                                icon={<SignoutIcon />}
                                                onClick={onSignOutClick}
                                            >
                                                <FormattedMessage
                                                    id="global.button.log_out"
                                                    defaultMessage="Log Out"
                                                />
                                            </StokkeMenuItem>
                                        </StokkeMenuList>
                                    </Menu>
                                )}

                                {wishlistEnabled && (
                                    <IconButton
                                        as="a"
                                        href={wishlistHref}
                                        position="relative"
                                        aria-label={
                                            showWishlistIcon
                                                ? intl.formatMessage({
                                                      id: 'header.button.assistive_msg.wishlist_has_items',
                                                      defaultMessage: 'Wishlist (items present)'
                                                  })
                                                : intl.formatMessage({
                                                      id: 'header.button.assistive_msg.wishlist',
                                                      defaultMessage: 'Wishlist'
                                                  })
                                        }
                                        icon={
                                            <>
                                                <HeartIcon
                                                    {...styles.icon}
                                                    data-testid="header-wishlist-icon"
                                                ></HeartIcon>

                                                {showWishlistIcon && (
                                                    <Box
                                                        position="absolute"
                                                        width="6px"
                                                        top="0"
                                                        right="-7px"
                                                    >
                                                        <svg
                                                            viewBox="0 0 10 10"
                                                            role="presentation"
                                                        >
                                                            <circle
                                                                fill="black"
                                                                stroke="black"
                                                                cx="5"
                                                                cy="5"
                                                                r="3"
                                                            />
                                                        </svg>
                                                    </Box>
                                                )}
                                            </>
                                        }
                                        variant="unstyled"
                                        {...styles.iconButton}
                                    />
                                )}

                                {site?.isContentOnlySite ? (
                                    <IconButton
                                        as="a"
                                        href={storeLocatorHref}
                                        target="_blank"
                                        rel="noopener"
                                        aria-label={intl.formatMessage({
                                            id: 'header.button.assistive_msg.storefinder',
                                            defaultMessage: 'Storefinder'
                                        })}
                                        icon={
                                            <StorefinderIcon
                                                {...styles.icon}
                                                data-testid="header-storefinder-icon"
                                            />
                                        }
                                        variant="unstyled"
                                        {...styles.iconButton}
                                    />
                                ) : (
                                    <IconButton
                                        as="a"
                                        href={buildUrl(CART_HREF)}
                                        aria-label={intl.formatMessage(
                                            {
                                                id: 'header.button.assistive_msg.my_cart_with_num_items',
                                                defaultMessage:
                                                    'My cart, number of items: {numItems}'
                                            },
                                            {numItems: totalItems}
                                        )}
                                        icon={
                                            <Box {...styles.cartIconContainer}>
                                                <BasketIcon
                                                    {...styles.icon}
                                                    data-testid="header-basket-icon"
                                                />
                                                {basket && !basketLoading && (
                                                    <Badge {...styles.cartBadge}>
                                                        <VisuallyHidden>
                                                            {intl.formatMessage({
                                                                id: 'header.button.assistive_msg.number_items',
                                                                defaultMessage: 'Number of items: '
                                                            })}
                                                        </VisuallyHidden>
                                                        {totalItems}
                                                    </Badge>
                                                )}
                                            </Box>
                                        }
                                        variant="unstyled"
                                        {...styles.cartButton}
                                        onClick={onMyCartClick}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </StylesProvider>
    )
})

if (process.env.NODE_ENV !== 'production') {
    Header.displayName = 'Header'
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onMyAccountMenuOpen: PropTypes.func,
    onMyCartClick: PropTypes.func,
    onSearchClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ])
}

export default Header
